
import { Inject, Injectable, NgZone, DOCUMENT } from '@angular/core';
import { SwUpdate } from '@angular/service-worker';

@Injectable({ providedIn: 'root' })
export class AppRefreshService {
  private reloadScheduled = false;

  public constructor(
    private readonly swUpdate: SwUpdate,
    private readonly ngZone: NgZone,
    @Inject(DOCUMENT) private readonly document: Document
  ) {
    this.handleChunkLoadFailures();
    this.handleServiceWorkerUpdates();
  }

  private handleChunkLoadFailures(): void {
    const win = this.document.defaultView;

    if (!win) {
      return;
    }

    win.addEventListener(
      'error',
      (event: ErrorEvent) => {
        if (this.isChunkLoadError(event.error ?? event.message)) {
          event.preventDefault();
          this.scheduleReload();
        }
      },
      true
    );

    win.addEventListener(
      'unhandledrejection',
      (event: PromiseRejectionEvent) => {
        if (this.isChunkLoadError(event.reason)) {
          event.preventDefault();
          this.scheduleReload();
        }
      }
    );
  }

  private handleServiceWorkerUpdates(): void {
    if (!this.swUpdate.isEnabled) {
      return;
    }

    this.swUpdate.versionUpdates.subscribe((event) => {
      if (event.type === 'VERSION_READY') {
        this.scheduleReload();
      }
    });

    this.swUpdate.unrecoverable.subscribe(() => this.scheduleReload());
  }

  private isChunkLoadError(error: unknown): boolean {
    if (!error) {
      return false;
    }

    const errorLike =
      typeof error === 'string'
        ? ({ message: error } as Partial<Error>)
        : error instanceof Error
        ? error
        : (error as Partial<Error>);

    const message = errorLike?.message ?? '';
    const name = errorLike?.name ?? '';

    return (
      name === 'ChunkLoadError' ||
      /Loading chunk \d+ failed/i.test(message) ||
      /ChunkLoadError/i.test(message)
    );
  }

  private scheduleReload(): void {
    if (this.reloadScheduled) {
      return;
    }

    this.reloadScheduled = true;

    this.ngZone.runOutsideAngular(async () => {
      await this.activateUpdate();
      await this.clearCaches();
      this.document.location.reload();
    });
  }

  private async activateUpdate(): Promise<void> {
    if (!this.swUpdate.isEnabled) {
      return;
    }

    try {
      await this.swUpdate.activateUpdate();
    } catch (error) {
      console.warn('Unable to activate service worker update', error);
    }
  }

  private async clearCaches(): Promise<void> {
    const win = this.document.defaultView;

    if (!win || !('caches' in win)) {
      return;
    }

    try {
      const cacheNames = await win.caches.keys();
      await Promise.all(
        cacheNames.map((cacheName) => win.caches.delete(cacheName))
      );
    } catch (error) {
      console.warn('Unable to clear caches before reload', error);
    }
  }
}
